import { useEffect, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { Printer } from "react-bootstrap-icons";
import { LoadingBlock, ErrorBlock } from "../../common/ui/StateBlock";
import { formatDate, display, isBlank } from "../../common/format";
import { SELLER, geoWords, gelNum } from "./invoiceShared";
import "./Auction.css";
import "../../common/ui/ui.css";

// Deposit top-up invoice. The bidder enters the maximum amount they want to be
// able to bid up to; the invoice asks for 10% of it to be paid into the
// company's bank account, which the operator then credits to their deposit.
// Matches the official "შპს ქარ ექსპრესი" invoice template.

const DEPOSIT_RATE = 0.1; // 10%

const AuctionDepositInvoice = () => {
    const location = useLocation();
    const history = useHistory();
    const [me, setMe] = useState(null);
    const [state, setState] = useState("loading");

    const params = new URLSearchParams(location.search);
    const maxBid = Math.round(Number(params.get("max")) || 0);
    const amount = Math.round(maxBid * DEPOSIT_RATE);

    useEffect(() => {
        let alive = true;
        axios.get("/api/auction/me")
            .then((res) => { if (alive) { setMe(res.data.user); setState("ready"); } })
            .catch((err) => {
                if (!alive) return;
                if (err && err.response && err.response.status === 401) { history.replace("/auction/login"); return; }
                setState("error");
            });
        return () => { alive = false; };
    }, [history]);

    if (state === "loading") return <div className="container auction-narrow"><LoadingBlock label="იტვირთება..." /></div>;
    if (state === "error" || !me) return <div className="container auction-narrow"><ErrorBlock title="ვერ მოიძებნა" message="მომხმარებელი ვერ მოიძებნა." /></div>;
    if (!(maxBid > 0)) {
        return (
            <div className="container auction-narrow auction-invoice-page">
                <ErrorBlock title="არასწორი თანხა" message="ინვოისის დასაგენერირებლად მიუთითეთ ბიდის მაქსიმალური თანხა." />
                <div style={{ textAlign: "center", marginTop: 16 }}><Link to="/auction/account" className="cx-btn cx-btn-primary">ანგარიშზე დაბრუნება</Link></div>
            </div>
        );
    }

    const isCompany = me.account_type === "company";
    const buyerName = isCompany ? display(me.company_name) : `${me.name || ""} ${me.surname || ""}`.trim();
    const buyerId = isCompany ? me.company_id : me.personal_id;
    const today = new Date();
    const due = new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    const invoiceNo = `CX-${today.getFullYear()}${pad(today.getMonth() + 1)}${pad(today.getDate())}-${me.id}`;

    return (
        <div className="container auction-invoice-page">
            <div className="auction-invoice-toolbar no-print">
                <Link to="/auction/account" className="auction-back">← ჩემი ანგარიში</Link>
                <button className="cx-btn cx-btn-primary" onClick={() => window.print()}><Printer /> ჩამოტვირთვა (PDF)</button>
            </div>

            <div className="cx-card auction-invoice-doc auction-dinv">
                {/* Top meta */}
                <div className="auction-dinv-top">
                    <div className="auction-dinv-brand">
                        <span className="auction-dinv-logo"><img src="/static/icons/logo.png" alt="CAR EXPRESS" /></span>
                        <div className="auction-muted auction-dinv-tagline">ონლაინ აუქციონი</div>
                    </div>
                    <dl className="auction-dinv-meta">
                        <div><dt>ინვოისი №</dt><dd>{invoiceNo}</dd></div>
                        <div><dt>თარიღი</dt><dd>{formatDate(today)}</dd></div>
                        <div><dt>გადახდის ვადა</dt><dd>{formatDate(due)}</dd></div>
                        <div><dt>ვალუტა</dt><dd>ლარი (GEL)</dd></div>
                    </dl>
                </div>

                <h1 className="auction-dinv-title">ინვოისი / ანგარიშ-ფაქტურა</h1>

                {/* Parties */}
                <div className="auction-dinv-parties">
                    <div className="auction-dinv-party">
                        <h4>გამყიდველი</h4>
                        <div className="auction-dinv-row"><span>დასახელება</span><strong>{SELLER.name}</strong></div>
                        <div className="auction-dinv-row"><span>საიდ. კოდი</span><span>{SELLER.idCode}</span></div>
                        <div className="auction-dinv-row"><span>ბანკი</span><span>{SELLER.bank}</span></div>
                        <div className="auction-dinv-row"><span>IBAN</span><span>{SELLER.iban}</span></div>
                    </div>
                    <div className="auction-dinv-party">
                        <h4>მყიდველი</h4>
                        <div className="auction-dinv-row"><span>{isCompany ? "დასახელება" : "სახელი, გვარი"}</span><strong>{buyerName || "—"}</strong></div>
                        <div className="auction-dinv-row"><span>{isCompany ? "საიდ. №" : "პირადი №"}</span><span>{display(buyerId)}</span></div>
                        <div className="auction-dinv-row"><span>მისამართი</span><span>{display(me.city)}</span></div>
                        <div className="auction-dinv-row"><span>ტელეფონი</span><span>{display(me.phone)}</span></div>
                    </div>
                </div>

                {/* Services */}
                <div className="auction-dinv-sectitle">მომსახურება</div>
                <table className="auction-invoice-table auction-dinv-table">
                    <thead>
                        <tr>
                            <th>№</th>
                            <th>დასახელება</th>
                            <th className="ar">ლოტის ღირებულება (GEL)</th>
                            <th className="ar">%</th>
                            <th className="ar">თანხა (GEL)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>ონლაინ აუქციონის ბალანსის შევსება (10%)</td>
                            <td className="ar">{gelNum(maxBid)}</td>
                            <td className="ar">10%</td>
                            <td className="ar">{gelNum(amount)}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="auction-dinv-total"><span>სულ:</span><strong>{gelNum(amount)} ₾</strong></div>
                <p className="auction-dinv-words">თანხა სიტყვიერად: <strong>{geoWords(amount)} ლარი</strong></p>

                {/* Payment instruction */}
                <div className="auction-dinv-instr">
                    <h4>გადახდის ინსტრუქცია</h4>
                    <p>
                        თანხა ჩაირიცხოს {SELLER.name}-ის ანგარიშზე <strong>{SELLER.iban}</strong> ({SELLER.bank}).
                        გადახდის დანიშნულებაში მიუთითეთ ინვოისის ნომერი <strong>{invoiceNo}</strong> და „ონლაინ აუქციონის ბალანსი 10%“.
                    </p>
                    <p className="auction-muted">გადახდის დადასტურების შემდეგ თანხა აისახება თქვენს სააუქციონო დეპოზიტზე.</p>
                </div>

                {/* Signatures */}
                <div className="auction-dinv-sign">
                    <div>
                        <div className="auction-dinv-row"><span>გამცემი</span><span>{SELLER.name}</span></div>
                        <div className="auction-dinv-sline">ხელმოწერა: ____________________</div>
                        <div className="auction-muted auction-dinv-ba">ბ.ა.</div>
                    </div>
                    <div>
                        <div className="auction-dinv-row"><span>მიმღები</span><span>{buyerName || "—"}</span></div>
                        <div className="auction-dinv-sline">ხელმოწერა: ____________________</div>
                    </div>
                </div>
            </div>

            {!isBlank(me.email) && <p className="auction-muted no-print" style={{ textAlign: "center", marginTop: 14 }}>კითხვების შემთხვევაში: info@carexpress.ge</p>}
        </div>
    );
};

export default AuctionDepositInvoice;
